/** Assembles the Reach from a seed: placement → relays → anchors → systems (+ vein guarantee) → minerals (+ guarantee) → names (M3 spec §3, D-033). */
import { loadGalaxyConfig } from '../data/galaxyConfig'
import type { GalaxyConfig } from '../data/galaxyConfig'
import { assignAnchors } from './anchors'
import { applyMineralGuarantee, applyRefractoryGuarantee } from './mineralGuarantee'
import { applyMinerals, applyRefractory } from './minerals'
import { nameSystems } from './names'
import { placeSystems, systemId } from './placement'
import { buildRelays, openRoute } from './relays'
import { encodeSeed } from './seed'
import { generateSystem } from './systems'
import type { Archetype, Galaxy, Relay, StarSystem } from './types'
import { VEIN, veinConversions } from './veins'

/** Pure and deterministic: the same seed and config always give a deep-equal galaxy. Load-time code. */
export function generateGalaxy(seed: number, cfg: GalaxyConfig = loadGalaxyConfig()): Galaxy {
  const s = seed >>> 0
  const placed = placeSystems(s, cfg)
  const built = buildRelays(s, placed, cfg)
  const anchors = assignAnchors(s, placed, built, cfg)
  // Anchors with onlineBias 1 (the megacorp HQ) must be reachable online at the start: open a route if needed.
  const home = anchors.indexOf('home-gate')
  const relays = anchors.reduce<Relay[]>(
    (rs, a, i) => (a !== null && cfg.anchors[a].onlineBias === 1 ? openRoute(placed, rs, home, i, cfg) : rs),
    built,
  )
  const touching = new Map<string, Relay[]>(placed.map((_, i) => [systemId(i), []]))
  for (const r of relays) {
    touching.get(r.a)?.push(r)
    touching.get(r.b)?.push(r)
  }
  const gen = (i: number, forced: Archetype | null): StarSystem =>
    generateSystem(s, i, placed[i].region, placed[i].mapPos, touching.get(systemId(i)) ?? [], anchors[i], cfg, forced)
  const rolled = placed.map((_, i) => gen(i, null))
  // The vein guarantee (D-086): a converted system is re-rolled as if it had rolled the vein archetype.
  const veins = new Set(veinConversions(s, rolled, cfg))
  const veined = rolled.map((sys, i) => (veins.has(i) ? gen(i, VEIN) : sys))
  // M8 5(a) mineral post-pass (MC-a2a): signatures, grades and a separate mineralWeights; oreWeights never moves.
  // The mineral guarantee (MC-a3): enough titanium/platinum signature systems online from home.
  // 5(e) (MC-e2): the Mk III refractory ore rolls last, on its own stream, so a v6 world's signatures and grades stay put.
  // 5(e) MC-e6b: then at least one refractory-signature Deep system online from home (never a vein).
  const mineral = applyMineralGuarantee(s, applyMinerals(s, veined, cfg), relays, home, cfg)
  const systems = applyRefractoryGuarantee(s, mineral, applyRefractory(s, mineral, cfg), relays, home, cfg)
  const names = nameSystems(s, anchors, cfg)
  return {
    seed: s,
    seedCode: encodeSeed(s),
    systems: systems.map((sys, i) => ({ ...sys, name: names[i] })),
    relays,
    homeSystemId: systemId(home),
  }
}
